import styled from '@emotion/styled';
import { Col, Modal } from 'antd';
import React from 'react';
import { Trans, withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { AppState } from 'src/config/appState';
import { StepButtons, StepButtonsProps } from 'src/modules/programs/components/StepButtons';
import * as S from 'src/modules/programs/stepComponents/styles';
import studyingIcon from 'src/public/img/undraw-studying-s-3-l-7.svg';
import { Button } from 'src/shared/components/Button';
import { color, font, layout } from 'src/shared/styles/variables';

import {
    changeValuesAssessmentAreaField,
    resetAreaAdditionalRate,
    submitValuesAssessmentsData,
} from '../actions/weekTwoActions';
import { AreaRating, AreaRatingContent } from '../components/AreaRating';
import { ValuesAssessmentDomain } from '../models/weekTwo';
import {
    getValuesAssessments,
    getValuesAssessmentsAreasAdditionalRatePoints,
} from '../selectors/weekTwoSelectors';

const LegendWrapper = styled(Col)({
    padding: '1rem',
});

const LegendContent = styled(AreaRatingContent)({
    display: 'flex',
});

interface DotProps {
    additional?: boolean;
}

const Dot = styled('div')(
    (props: DotProps) => ({
        color: color.baliHai,
        paddingLeft: '2.25rem',
        position: 'relative',
        ':not(:first-child)': {
            marginLeft: '3rem',
        },
        '&:before': {
            content: '""',
            display: 'block',
            position: 'absolute',
            borderRadius: '100%',
            width: 20,
            height: 20,
            top: -2,
            left: 0,
            backgroundColor: props.additional ? color.springGreen : color.shakeSpeare,
        },
    }),
    font.heavy(font.size.base),
);

const StyledModal = styled(Modal)({
    '& .ant-modal-content': {
        maxWidth: layout.modalWidth.wide,
        margin: '0 auto',
        textAlign: 'center',
    },
});

interface ModalIconProps {
    iconUrl: string;
}

const ModalIcon = styled('i')<ModalIconProps>(props => ({
    display: 'block',
    backgroundImage: `url(${props.iconUrl})`,
    height: '12rem',
    width: '12rem',
    margin: '0 auto 2rem',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
}));

const ModalTitle = styled('h3')(
    {
        width: '100%',
        margin: '0 0 1.5rem',
        color: color.bayOfMany,
    },
    font.heavy(font.size.extraLarge),
);

const ModalText = styled('p')(
    {
        color: color.tundora,
        marginBottom: '2rem',
    },
    font.book(font.size.large),
);

interface StateProps {
    valuesAssessments: ValuesAssessmentDomain[];
    availablePoints: number;
}

interface ActionsProps {
    changeValuesAssessmentAreaField: typeof changeValuesAssessmentAreaField;
    submitValuesAssessmentsData: typeof submitValuesAssessmentsData;
    resetAreaAdditionalRate: typeof resetAreaAdditionalRate;
}

type Props = StepButtonsProps & StateProps & ActionsProps & WithTranslation;

interface State {
    isModalOpen: boolean;
}

export class ValuesAreasRatingStepContainer extends React.Component<Props, State> {
    public state: State = {
        isModalOpen: false,
    };

    public handleToggleModal = () => {
        this.setState(state => ({ isModalOpen: !state.isModalOpen }));
    };

    public handleAreaRateExtend = (domainTitle: string, value: number) => {
        this.props.changeValuesAssessmentAreaField({ domainTitle, value, field: 'additionalRate' });
    };

    private handleSubmit = () => {
        const { valuesAssessments, availablePoints } = this.props;

        if (availablePoints > 0) {
            return;
        }

        this.props.submitValuesAssessmentsData(valuesAssessments);
        this.props.goToNextStep();
    };

    private handlePreviousClick = () => {
        this.props.resetAreaAdditionalRate();
        this.props.goToPreviousStep();
    };

    public render(): JSX.Element {
        return (
            <>
                <S.ContentRow>
                    <S.ContentColumn>
                        <S.StepHeading>
                            {this.props.t('week-2.rate-your-life-areas.heading')}
                        </S.StepHeading>
                    </S.ContentColumn>
                </S.ContentRow>
                <S.ContentRow>
                    <S.ContentColumn>
                        <S.StepDescription small>
                            {this.props.t('week-2.rate-your-life-areas.description.1')}
                        </S.StepDescription>
                        <S.StepDescription small>
                            <Trans i18nKey="week-2.rate-your-life-areas.description.2">
                                text
                                <S.Strong>text</S.Strong>
                                text
                            </Trans>
                        </S.StepDescription>
                    </S.ContentColumn>
                </S.ContentRow>
                <S.ContentRow type="flex">
                    {this.props.valuesAssessments.map(domain => (
                        <AreaRating
                            key={domain.title}
                            domain={domain}
                            onRateExtend={this.handleAreaRateExtend}
                            availablePoints={this.props.availablePoints}
                        />
                    ))}
                    <LegendWrapper span={24}>
                        <LegendContent>
                            <Dot>{this.props.t('week-2.rate-your-life-areas.currentPoints')}</Dot>
                            <Dot additional>
                                {this.props.t('week-2.rate-your-life-areas.additionalPoints')}
                            </Dot>
                        </LegendContent>
                    </LegendWrapper>
                </S.ContentRow>
                <StyledModal
                    closable={false}
                    visible={this.state.isModalOpen}
                    onCancel={this.handleToggleModal}
                    footer={null}
                    width="auto"
                    centered
                >
                    <ModalIcon iconUrl={studyingIcon} />
                    <ModalTitle>
                        {this.props.t('week-2.rate-your-life-areas.modal.heading')}
                    </ModalTitle>
                    <ModalText>
                        {this.props.t('week-2.rate-your-life-areas.modal.description')}
                    </ModalText>
                    <Button onClick={this.handleSubmit}>Continue</Button>
                </StyledModal>
                <StepButtons
                    onPreviousClick={this.handlePreviousClick}
                    onNextClick={this.handleToggleModal}
                    hasNextStep
                    nextDisabled={!!this.props.availablePoints}
                />
            </>
        );
    }
}

const mapStateToProps = (state: AppState): StateProps => ({
    valuesAssessments: getValuesAssessments(state),
    availablePoints: 3 - getValuesAssessmentsAreasAdditionalRatePoints(state), // Project's specification requires exactly 3 areas to be chosen
});

export const ValuesAreasRatingStep = withTranslation('cp')(
    connect(mapStateToProps, {
        changeValuesAssessmentAreaField,
        submitValuesAssessmentsData,
        resetAreaAdditionalRate,
    })(ValuesAreasRatingStepContainer),
);
